import * as React from 'react';
import OlMap from 'ol/Map';
import OlLayerImage from 'ol/layer/Image';
import OlImageSource from 'ol/source/Image';
import OlLayerTile from 'ol/layer/Tile';
import OlTileSource from 'ol/source/Tile';
import OlSourceImageWMS from 'ol/source/ImageWMS';
import OlSourceTileWMS from 'ol/source/TileWMS';
import OlSourceWMTS, { Options } from 'ol/source/WMTS';
import { MapComponent } from '@terrestris/react-geo';
import { Collapse } from 'antd';
import '../css/MyLayerSwitcher.css';
import 'antd/dist/antd.min.css';

/*
Aquesta versió necessita que al mapa otiginal nomé hi hagi un dels layers de la llista
*/

/**
 * @export
 * @interface LayerSwitcherProps
 * @extends {React.HTMLAttributes<HTMLDivElement>}
 */
export interface BaseProps {
    /**
     * The layers to be available in the switcher.
     */
    layers: Array<OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource>>;

    /**
     * The main map the layers should be synced with.
     */
    map: OlMap;

    /**
     * Callback to inform of the baseLayer selected
     */
    onBaseLayerChanged?: (layerSelected: OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource>) => void;

    /**
     * Index del layer dins el mapa que s'intercambiarà
     */
    layerindex?: number;
}

interface LayerSwitcherState {
    visibleLayerIndex: number;
    collapseKey: string | string[];
}

export type LayerSwitcherProps = BaseProps & React.HTMLAttributes<HTMLDivElement>;

/**
 * Class representing the LayerSwitcher.
 * A basic component to switch between the passed layers.
 * This is most likely to be used for the backgroundlayer.
 *
 * @class LayerSwitcher
 * @extends React.Component
 */
export class MyLayerSwitcher2 extends React.Component<LayerSwitcherProps, LayerSwitcherState> {

    /**
     * The internal maps of the LayerSwitcher
     */
    _maps: OlMap[] = [];

    /**
     * The internal layers of the LayerSwitcher
     */
     _layerClones: Array< OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource>> = [];

    /**
     * The className of this component.
     */
    static readonly _className = 'react-geo-layer-switcher';

    /**
     * Per defecte el Layer que intercambiem és el 0
     */
    static defaultProps = {
        layerindex: 0
    };

    /**
     * Creates the LayerSwitcher.
     *
     * @constructs LayerSwitcher
     */
    constructor(props: LayerSwitcherProps) {
        super(props);
        let i: number;
        for (i = 0; i < props.layers.length; i++) {
            this._maps.push(this.getMap());
        }
        this.state = {
            visibleLayerIndex: 0,
            collapseKey: []
        };
    }

    /**
     * A react lifecycle method called when the component did mount.
     */
    componentDidMount() {
        this.setMapsLayers();
    }

    /**
     * Destroy all map specific stuff when umounting the component.
     *
     * @memberof LayerSwitcher
     */
    componentWillUnmount() {
        this._maps.forEach((m) => {
            m.getLayers().clear();
            m.setTarget(null);
            m = null;
        });
    }

    cloneOlSourceImageWMS(source: OlSourceImageWMS): OlSourceImageWMS {
        return new OlSourceImageWMS({
            url: source.getUrl(), 
            params: source.getParams(), 
            projection: source.getProjection()
        });
    }

    cloneOlSourceTileWMS(source: OlSourceTileWMS): OlSourceTileWMS {
        return new OlSourceTileWMS({
            url: source.getUrls()[0],
            params: source.getParams(), 
            projection: source.getProjection()
        });
    }
    
    cloneOlSourceWMTS(cap: Options): OlSourceWMTS {
        return new OlSourceWMTS(cap);
    }
    
    /**
     * Clones a layer
     *
     * @param layer The layer to clone.
     * @returns The cloned layer.
     */
    cloneLayer = (layer: OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource>): OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource> => {
        let layerClone: OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource>;
        if (layer.get('singleTile')) {
            layerClone = new OlLayerImage<OlImageSource>({
                ...layer.getProperties(),
                source: this.cloneOlSourceImageWMS(((layer as OlLayerImage<OlImageSource>).getSource() as OlSourceImageWMS)),
                // properties: {
                //     originalLayer: layer
                // },
            });
        } else {
            layerClone = new OlLayerTile<OlTileSource>({
                ...layer.getProperties(),
                source: layer.get('cap') ? this.cloneOlSourceWMTS(layer.get('cap')) : this.cloneOlSourceTileWMS(((layer as OlLayerTile<OlTileSource>).getSource() as OlSourceTileWMS)),
                // properties: {
                //     originalLayer: layer
                // },
            });
        }
        // layerClone.set('originalLayer', layer);
        return layerClone;
    }

    /**
     * Adds the layers to the preview map and sets the visibleLayerIndex.
     */
    setMapsLayers = () => {
        const {
            layers
        } = this.props;
        if (layers.length < 3) {
            console.log(`MyLayerSwitcher2 requires three or more layers.`);
        }
        this._maps.forEach((m, i) => {
            const l = this.cloneLayer(layers[i])
            this._layerClones.push(l);
            l.setVisible(false);
            m.addLayer(l);
        });
    }

    /**
     * Sets the visiblity of the layers in the props.map and this._map.
     * Also sets the previewLayer in the state.
     *
     */
    updateLayersVisibility = (newLayerIndex: number) => {
        const {
            map,
            layers,
            layerindex
        } = this.props;
        layers.forEach((l, i) => {
            if (newLayerIndex === i) {
                l.setVisible(true);
                map.getLayers().insertAt(layerindex, l);
                this.setState({ visibleLayerIndex: i });
            } else {
                l.setVisible(false);
            }
        });
        map.getLayers().removeAt(layerindex + 1);
    }

    /**
     * Constructs this._maps
     */
    getMap = (): OlMap => {
        const {
            map
        } = this.props;
        return new OlMap({
            view: map.getView(),
            controls: []
        });
    }

    /**
     * Clickhandler for the overview switch.
     */
    onSwitcherClick = (mindex: number, evt: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        evt.stopPropagation();
        this.setState({ collapseKey: [] });
        this.enableMaps(false);
        this.updateLayersVisibility(mindex);
        if (this.props.onBaseLayerChanged) {
            const {
                layers
            } = this.props;
            this.props.onBaseLayerChanged(layers[mindex]);
        }
    }

    /**
     * Activa o desactiva els mapes
     */
    enableMaps = (enable: boolean) => {
        this._layerClones.forEach((l) => {
            l.setVisible(enable);
        });
    }

    /**
     * Hem obert o tancat el LayerSitcher
     */
    handleCollapseOnChange = (key: string | string[]) => {
        this.setState({ collapseKey: key });
        // Si el collapse està plegat, desactivem els mapes
        this.enableMaps(!(!key || key.length === 0));
    }

    /**
     * The render function.
     */
    render() {
        const {
            layers,
            // eslint-disable-next-line react/prop-types
            style,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            onBaseLayerChanged,
            ...passThroughProps
        } = this.props;

        return (
            <Collapse style={style} activeKey={this.state.collapseKey} onChange={this.handleCollapseOnChange}>
                <Collapse.Panel header="Base" key="bpanel1">
                    <div
                        className={MyLayerSwitcher2._className}
                        style={{
                            position: 'relative',
                            bottom: '0px',
                            left: '0px',
                            zIndex: 2,
                            margin: '-12px'
                        }}
                        {...passThroughProps}
                    >
                        {this._maps.map((mp, index) => {
                            return (<div style={{ margin: '4px' }}
                                key={"ls2-" + index}
                                className={index === this.state.visibleLayerIndex ? "clipSelected" : "clip"}
                                onClick={this.onSwitcherClick.bind(this, index)}
                            >
                                <MapComponent
                                    className="lsm"
                                    mapDivId={"layer-switcher-map-" + index}
                                    map={mp}
                                />
                                <span className={"layer-title2"} style={{ bottom: ((layers.length - index - 1) * 84) + 'px' }}>
                                    {layers[index].get('name')}
                                </span>
                            </div>)
                        })}
                    </div>
                </Collapse.Panel>
            </Collapse>
        );
    }
}

export default MyLayerSwitcher2;